import BottomBar from './BottomBar';

import { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { initialFormValues } from '../../schema';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { FormMode } from '@/types/form';

import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import { OtherTab } from './OtherTab';
import { ThueTab } from './ThueTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    handleRowClick: detailHandleRowClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange
  } = useDetailRows(initialData?.chi_tiet_data || []);
  const {
    rows: taxRows,
    selectedRowUuid: taxSelectedRowUuid,
    handleRowClick: taxHandleRowClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handlePasteRow: taxHandlePasteRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange
  } = useDetailRows(initialData?.thue_data || []);
  const { state, actions } = useFormFieldState(initialData);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const detail = useMemo(() => {
    return initialData?.chi_tiet_data || detailRows;
  }, [initialData, detailRows]);

  const { tongPSno, tongPSCo } = useMemo(() => {
    return calculateTotals(detail);
  }, [detail]);

  const handleSubmit = (data: MuaHangRecord) => {
    const formData = transformFormData(data, state, detailRows, taxRows);
    console.log('formData: ', { ...formData, unit_id: entityUnit?.uuid });
    onSubmit?.({ ...formData, unit_id: entityUnit?.uuid });
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={initialData || initialFormValues}
        title={title}
        actionButtons={actionButtons}
        subTitle='Chứng từ phải trả khác, chứng từ bù trừ công nợ'
        onSubmit={handleSubmit}
        onClose={handleClose}
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <div>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            )
          },
          ...(formMode === 'view'
            ? [
                {
                  id: 'history',
                  label: 'Lịch sử',
                  component: <HistoryTab />
                }
              ]
            : [])
        ]}
        headerTabActions={
          formMode !== 'add' && initialData?.uuid ? (
            <InfoDropdown uuid={initialData.uuid} ma_ct={initialData?.chung_tu} />
          ) : null
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  onRowClick={detailHandleRowClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  onRepeatForRows={handleRepeatDetailRows}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <ThueTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  onRowClick={taxHandleRowClick}
                  onAddRow={taxHandleAddRow}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onPasteRow={taxHandlePasteRow}
                  onMoveRow={taxHandleMoveRow}
                  onCellValueChange={taxHandleCellValueChange}
                />
              )
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={activeTab === 'info' && <BottomBar tongPSno={tongPSno} tongPSCo={tongPSCo} ty_gia={state.tyGia} />}
      />
    </>
  );
};

export default FormDialog;
